import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  Check, 
  Crown,
  ArrowLeft,
  Calculator,
  BarChart3,
  FileText,
  Users,
  MessageSquare,
  GitCompare,
  Loader2,
  Building2,
  Home,
  Landmark,
  HardHat,
  Briefcase,
  Sparkles,
  Zap,
  Shield,
  Star
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UpgradePage({ user, subscription, onLogout, onUpgrade }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [upgrading, setUpgrading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [addingRole, setAddingRole] = useState(null);
  const currentTier = subscription?.tier || user?.subscription_tier || "starter";
  const activeRoles = user?.extra_roles || [];

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const success = searchParams.get("success");
    
    if (sessionId && success === "true") {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/payments/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === "paid") {
          toast.success("Payment successful! Welcome aboard!");
          onUpgrade && onUpgrade();
          navigate("/dashboard");
          return;
        }

        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          toast.error("Payment verification timed out. Contact support if charged.");
          setCheckingPayment(false);
        }
      } catch (error) {
        console.error("Payment check failed:", error);
        setCheckingPayment(false);
      }
    };

    poll();
  };

  const handleAddRole = async (role) => {
    if (activeRoles.includes(role)) {
      // Already active — go to portal
      const portalPaths = { lender: "/lender/portal", realtor: "/realtor/dashboard", contractor: "/contractor/dashboard", title: "/title/dashboard" };
      navigate(portalPaths[role] || "/dashboard");
      return;
    }
    setAddingRole(role);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/add-role`, { role }, { withCredentials: true });
      toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} role activated! Switching to your portal...`);
      onUpgrade && onUpgrade();
      // Auto-navigate to portal
      const res = await axios.post(`${BACKEND_URL}/api/auth/portal-token/${role}`, {}, { withCredentials: true });
      navigate(res.data.redirect || "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add role");
    } finally {
      setAddingRole(null);
    }
  };

  const handleUpgrade = async (tier) => {
    if (tier === currentTier) {
      toast.info("You're already on this plan");
      return;
    }
    
    setUpgrading(tier);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/create-checkout-session`,
        { tier, origin_url: window.location.origin },
        { withCredentials: true }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
      setUpgrading(null);
    }
  };

  // Investor Plans — Starter removed, Pro renamed to Investor
  const investorPlans = [
    {
      name: "Investor",
      label: "For active dealmakers",
      price: "$149",
      period: "/month",
      priceSubtext: "Access deals, capital, and execution — all in one network.",
      description: "Operate inside the DealForge network",
      icon: Sparkles,
      color: "gold",
      highlighted: true,
      features: [
        "Create and manage deals",
        "Request capital",
        "Post to Execution Exchange",
        "Connect with lenders, title, and partners",
        "AI deal analysis & scoring",
        "Full deal execution capabilities",
        "PDF deal packets",
        "Buyer list management"
      ],
      current: currentTier === "pro" || currentTier === "investor",
      tier: "pro",
      cta: "Activate Investor Access"
    },
    {
      name: "Operator",
      label: "For teams and high-volume operators",
      price: "$299.99",
      period: "/month",
      priceSubtext: null,
      description: "For high-volume operators scaling deal flow",
      icon: Crown,
      color: "purple",
      features: [
        "Everything in Investor",
        "Team collaboration (5 seats)",
        "API access",
        "White-label reports",
        "Priority support",
        "Custom integrations",
        "Advanced analytics"
      ],
      current: currentTier === "operator",
      tier: "operator",
      cta: "Scale to Operator"
    }
  ];

  // Portal Plans
  const portalPlans = [
    {
      name: "Contractor",
      price: "$99.99",
      period: "/month",
      description: "For contractors & rehab specialists",
      icon: HardHat,
      color: "orange",
      features: [
        "Contractor portal access",
        "Bid on investor projects",
        "Project management tools",
        "Document storage",
        "Payment tracking",
        "Reviews & ratings"
      ],
      tier: "contractor",
      portal: true
    },
    {
      name: "Realtor",
      price: "$99.99",
      period: "/month",
      description: "For real estate agents",
      icon: Home,
      color: "green",
      features: [
        "Realtor portal access",
        "Listing management",
        "Transaction tracking",
        "Partner Network",
        "Title service requests",
        "Commission tracking"
      ],
      tier: "realtor",
      portal: true
    },
    {
      name: "Lender",
      price: "$299.99",
      period: "/month",
      description: "For private lenders & hard money",
      icon: Landmark,
      color: "blue",
      features: [
        "Lender portal access",
        "Deal flow from investors",
        "Loan pipeline management",
        "Document collection",
        "Automated underwriting",
        "Portfolio analytics"
      ],
      tier: "lender",
      portal: true
    },
    {
      name: "Title Company",
      price: "$199.99",
      period: "/month",
      description: "For title & closing companies",
      icon: Briefcase,
      color: "purple",
      features: [
        "Title portal access",
        "Marketplace bidding",
        "Order management",
        "Document handling",
        "Investor network access",
        "Revenue analytics"
      ],
      tier: "title",
      portal: true
    }
  ];

  const getColorClasses = (color, highlighted) => {
    if (highlighted) {
      return {
        border: "border-gold shadow-[0_0_30px_rgba(212,175,55,0.3)]",
        icon: "bg-gold/20 text-gold",
        button: "gold-gradient-btn"
      };
    }
    
    const colors = {
      blue: { border: "border-blue-500/30", icon: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]", button: "bg-blue-600 hover:bg-blue-500" },
      gold: { border: "border-gold/30", icon: "bg-gold/20 text-gold", button: "bg-[#D4AF37] hover:bg-[#C9A227] text-black" },
      purple: { border: "border-purple-500/30", icon: "bg-purple-500/20 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]", button: "bg-purple-600 hover:bg-purple-500 text-white" },
      orange: { border: "border-orange-500/30", icon: "bg-orange-500/20 text-orange-400", button: "bg-orange-600 hover:bg-orange-500 text-white" },
      green: { border: "border-green-500/30", icon: "bg-green-500/20 text-green-400", button: "bg-green-600 hover:bg-green-500 text-white" }
    };
    return colors[color] || colors.blue;
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-gold animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Verifying Payment...</h2>
          <p className="text-white/50">Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="upgrade-page">
      {/* Full-page header — no sidebar */}
      <header className="border-b border-white/[0.06] bg-black/60 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <img src="/logo.png" alt="DealForge" className="h-8 w-auto" onError={e => { e.target.style.display='none'; }} />
            <span className="text-xl font-black text-white tracking-tight">DEAL<span className="text-[#D4AF37]">FORGE</span></span>
          </Link>
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="upgrade-main">
        {/* Hero */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full text-[#D4AF37] text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Network Access
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight">
            Access. Participate.<br />
            <span className="text-[#D4AF37]">Scale.</span>
          </h1>
          <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto">
            Access deals, capital, and execution — all in one network.
          </p>
        </div>

        {/* Tab Switcher — removed, portals now shown inline below */}

        {/* Investor Plans — 2 plans, centered grid */}
        <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-12 max-w-3xl mx-auto">
              {investorPlans.map((plan, idx) => {
                const colors = getColorClasses(plan.color, plan.highlighted);
                return (
                  <div 
                    key={idx}
                    className={`relative bg-[#0A0A0A]/80 backdrop-blur-sm/50 border-2 ${colors.border} p-5 md:p-6 rounded-2xl transition-all hover:scale-[1.02] ${
                      plan.highlighted ? "md:-mt-4 md:mb-4" : ""
                    }`}
                  >
                    {plan.highlighted && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-xs font-bold uppercase tracking-wider bg-gold text-black rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> Most Popular
                      </div>
                    )}
                    
                    {plan.current && (
                      <div className="absolute top-4 right-4 px-2 py-1 text-xs font-bold uppercase bg-gold/20 text-gold rounded">
                        Current
                      </div>
                    )}

                    <div className={`w-12 h-12 rounded-2xl ${colors.icon} flex items-center justify-center mb-4`}>
                      <plan.icon className="w-6 h-6" />
                    </div>
                    
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/30 mb-1">{plan.label}</p>
                    <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                    <p className="text-sm text-white/50 mb-4">{plan.description}</p>
                    
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-white">{plan.price}</span>
                      <span className="text-white/50">{plan.period}</span>
                    </div>
                    {plan.priceSubtext && (
                      <p className="text-xs text-[#D4AF37]/70 mb-5 leading-relaxed">{plan.priceSubtext}</p>
                    )}
                    {!plan.priceSubtext && <div className="mb-5" />}
                    
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-white/70">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {plan.current ? (
                      <Button 
                        disabled
                        className="w-full bg-white/[0.08] text-white/50 rounded-2xl font-bold h-12"
                      >
                        Current Plan
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => handleUpgrade(plan.tier)}
                        disabled={upgrading === plan.tier}
                        className={`w-full rounded-2xl font-bold h-12 ${colors.button}`}
                        data-testid={`upgrade-${plan.tier}-btn`}
                      >
                        {upgrading === plan.tier ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          plan.cta || `Get ${plan.name}`
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Closing identity line */}
            <p className="text-center text-white/40 text-sm italic mb-10 mt-2">
              Early users don't just join the network — they define it.
            </p>

            {/* Investor Features Breakdown */}
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] rounded-2xl p-6 mb-8">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-gold" />
                  What You Get with Investor Access
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                  {[
                    { icon: Calculator, title: "Multi-Scenario Analysis", desc: "Compare wholesale, flip, BRRRR, and more strategies per deal" },
                    { icon: BarChart3, title: "AI Deal Scoring", desc: "Get confidence scores and risk analysis powered by AI" },
                    { icon: FileText, title: "PDF Deal Packets", desc: "Generate professional buyer-ready documents instantly" },
                    { icon: Users, title: "Buyer Management", desc: "Track buyers, their criteria, and match them to deals" },
                    { icon: MessageSquare, title: "SMS Campaigns", desc: "Send templated SMS blasts to leads and contacts" },
                    { icon: GitCompare, title: "Deal Comparison", desc: "Compare multiple deals side-by-side" }
                  ].map((feature, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="w-10 h-10 bg-gold/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white mb-1">{feature.title}</h3>
                        <p className="text-sm text-white/50">{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
          </>
        

        {/* Partner Portals — always visible */}
        <div className="mt-12 md:mt-16 mb-12">
          <div className="text-center mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
              <Users className="w-6 h-6 text-[#D4AF37]" /> Add Partner Portals
            </h2>
            <p className="text-white/50 text-sm md:text-base">Add a role to your account — no separate registration needed. Switch between portals from any dashboard.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
              {portalPlans.map((plan, idx) => {
                const colors = getColorClasses(plan.color);
                const roleKey = plan.tier;
                const isActive = activeRoles.includes(roleKey);
                const isLoading = addingRole === roleKey;
                return (
                  <div 
                    key={idx}
                    className={`relative bg-[#0A0A0A]/80 backdrop-blur-sm/50 border ${isActive ? "border-emerald-500/40 shadow-[0_0_20px_rgba(16,185,129,0.1)]" : colors.border} p-6 rounded-2xl transition-all hover:scale-[1.02]`}
                    data-testid={`portal-plan-${roleKey}`}
                  >
                    {isActive && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-bold uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" /> Active
                      </div>
                    )}
                    <div className={`w-12 h-12 rounded-2xl ${colors.icon} flex items-center justify-center mb-4`}>
                      <plan.icon className="w-6 h-6" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                    <p className="text-sm text-white/50 mb-4">{plan.description}</p>
                    
                    <div className="mb-6">
                      <span className="text-3xl font-bold text-white">{plan.price}</span>
                      <span className="text-white/50">{plan.period}</span>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-white/70">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      onClick={() => handleAddRole(roleKey)}
                      disabled={isLoading}
                      className={`w-full rounded-2xl font-bold h-11 text-white ${isActive ? "bg-emerald-600 hover:bg-emerald-500" : colors.button}`}
                      data-testid={`add-role-${roleKey}-btn`}
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : isActive ? (
                        "Go to Portal"
                      ) : (
                        "Add Role & Activate"
                      )}
                    </Button>
                  </div>
                );
              })}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Shield className="w-5 h-5 text-white/50" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: "Can I change plans later?",
                a: "Yes. You can move from Investor to Operator at any time. Changes take effect immediately."
              },
              {
                q: "What happens to my data if I downgrade?",
                a: "Your data is always preserved. If you move to Investor from Operator, team collaboration features will be limited but all your deals and history remain intact."
              },
              {
                q: "Is there a free trial?",
                a: "Founding beta members receive a 7-day free trial — full platform access, $0 due today. A card is required at signup. After 7 days, your rate of $99/month is locked in permanently."
              },
              {
                q: "Can I cancel anytime?",
                a: "Absolutely. Cancel anytime from your account settings. You'll retain access until the end of your billing period."
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] p-4 rounded-2xl">
                <h3 className="font-bold text-white mb-2">{faq.q}</h3>
                <p className="text-sm text-white/50">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
